import { createSlice } from '@reduxjs/toolkit';
import { PrinterState } from '../../types/printer';
import {
  createOutletPrinterThunk,
  fetchPrinterDeviceIdThunk,
  fetchInstalledPrintersThunk,
  fetchOutletPrintersThunk,
  fetchTakeAwayPrinterMappingsThunk,
  fetchSectionPrinterMappingsThunk,
  saveTakeAwayPrinterConfigThunk,
  saveSectionPrinterConfigThunk,
  testPrinterConnectionThunk,
} from './printerThunks';

const initialState: PrinterState = {
  outletPrinters: [],
  sectionMappings: [],
  takeAwayMappings: [],
  deviceId: null,
  installedPrinters: [],
  isLoading: false,
  isSaving: false,
  isTesting: false,
  error: null,
};

const printerSlice = createSlice({
  name: 'printers',
  initialState,
  reducers: {
    clearPrinterError: (state) => {
      state.error = null;
    },
    clearPrinterState: (state) => {
      state.outletPrinters = [];
      state.sectionMappings = [];
      state.takeAwayMappings = [];
      state.deviceId = null;
      state.installedPrinters = [];
      state.isLoading = false;
      state.isSaving = false;
      state.isTesting = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInstalledPrintersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInstalledPrintersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.installedPrinters = action.payload;
      })
      .addCase(fetchInstalledPrintersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch installed printers';
      })
      .addCase(fetchPrinterDeviceIdThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchPrinterDeviceIdThunk.fulfilled, (state, action) => {
        state.deviceId = action.payload;
      })
      .addCase(fetchPrinterDeviceIdThunk.rejected, (state, action) => {
        state.error = action.payload || 'Failed to resolve current device ID';
      })
      .addCase(fetchOutletPrintersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOutletPrintersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.outletPrinters = action.payload;
      })
      .addCase(fetchOutletPrintersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch outlet printers';
      })
      .addCase(createOutletPrinterThunk.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createOutletPrinterThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.outletPrinters.push(action.payload);
        state.outletPrinters.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(createOutletPrinterThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || 'Failed to create outlet printer';
      })
      .addCase(testPrinterConnectionThunk.pending, (state) => {
        state.isTesting = true;
        state.error = null;
      })
      .addCase(testPrinterConnectionThunk.fulfilled, (state) => {
        state.isTesting = false;
      })
      .addCase(testPrinterConnectionThunk.rejected, (state, action) => {
        state.isTesting = false;
        state.error = action.payload || 'Failed to print test page';
      })
      .addCase(fetchSectionPrinterMappingsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSectionPrinterMappingsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sectionMappings = action.payload;
      })
      .addCase(fetchSectionPrinterMappingsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch section mappings';
      })
      .addCase(fetchTakeAwayPrinterMappingsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTakeAwayPrinterMappingsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.takeAwayMappings = action.payload;
      })
      .addCase(fetchTakeAwayPrinterMappingsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload || 'Failed to fetch takeaway printer mappings';
      })
      .addCase(saveSectionPrinterConfigThunk.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveSectionPrinterConfigThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.sectionMappings = action.payload;
      })
      .addCase(saveSectionPrinterConfigThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || 'Failed to save section mappings';
      })
      .addCase(saveTakeAwayPrinterConfigThunk.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveTakeAwayPrinterConfigThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        state.takeAwayMappings = action.payload;
      })
      .addCase(saveTakeAwayPrinterConfigThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error =
          action.payload || 'Failed to save takeaway printer mappings';
      });
  },
});

export const { clearPrinterError, clearPrinterState } = printerSlice.actions;

export default printerSlice.reducer;
